import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  // The default query prefix for the API is `/api`
  baseUrl: "/api",
  prepareHeaders: (headers, { getState }) => {
    // Add the access token to API requests when available
    const accessToken = getState().auth.accessToken;
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

// Define the single API slice object for the `api` service
export const api = createApi({
  // The cache reducer expects to be added at `state.api` (the default)
  reducerPath: "api",

  baseQuery,

  // The `endpoints` represent operations and requests for this service.
  // The baseline endpoints are implemented here that are common to all
  // features. The app features extend this service with injectEndpoints().
  endpoints: (builder) => ({
    // Connectivity health check with api service
    getHealth: builder.query({
      query: () => "/health",
    }),

    // Create User from UI
    createUser: builder.mutation({
      query: (p) => ({
        url: "/create_web_user",
        method: "POST",
        params: {'username': p.username, 'password': p.password}
      })
    }),

    liveStreamModelCounts: builder.query({
      query: () => "/live_cams_model_streams_for_ui",
    }),

    liveStreams: builder.query({
      query: () => "/live_stream_history",
    }),

    // Login request
    login: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    // Chamaileon 
    chamaileonToken: builder.mutation({
      query: () => ({
        url: "/chamaileon_token",
        method: "POST",
      }),
    }),

    uploadS3: builder.mutation({
      query: (p) => ({
        url: "/s3-upload-url",
        method: "POST",
        params: {'title':p.title, 'category': p.category},
        body: p.body,
        formData: true,
      })
    }),

    allImages: builder.query({
      query: () => ({
        url: "/get_images",
      })
    }),

    getSummaries: builder.query({
      query: () => ({
        url: "/get_summaries",
      })
    }),

    deleteImage: builder.mutation({
      query: (p) => ({
        url: "/delete_image",
        method: "POST",
        params: {'image_id': p.image_id} 
      })
    }),

    editImage: builder.mutation({
      query: (p) => ({
        url: "/edit_image",
        method: "POST",
        params: {'image_id': p.image_id, "title": p.title, "category": p.category} 
      })
    }),

    allEmails: builder.query({
      query: () => ({
        url: "/get_emails",
      })
    }),

    addEmail: builder.mutation({
      query: (p) => ({
        url: "/add_email",
        method: "POST",
        body: {'json_body': p.json_body,'title': p.title},
        headers: { 
          'Content-Type': 'application/json'
        }
      })
    }),

    editEmail: builder.mutation({
      query: (p) => ({
        url: "/edit_email",
        method: "POST",
        body: {'email_id': p.email_id, "title": p.title, "json_body": p.json_body},
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }),

    sendEmail: builder.mutation({
      query: (p) => ({
        url: "/send_email",
        method: "POST",
        body: {'email_id': p.email_id, "recipient": p.recipient, "json_variables": p.json_variables},
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }),

    generateEmail: builder.mutation({
      query: (p) => ({
        url: "/generate_email",
        method: "POST",
        body: {"json_variables": p.json_variables, "id": p.id},
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }),

    generateHTML: builder.query({
      query: (p) => ({
        url: "/generate_html_email",
        method: "GET",
        params: {"id": p.id}
      })
    }),

    importHTMLemail: builder.mutation({
      query: (p) => ({
        url: "/import_html_email",
        method: "POST",
        body: p,
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }),

    jsonDownload: builder.query({
      query: (p) => ({
        url: "/get_json_email",
        method: "GET",
        params: {"id": p.id}
      })
    }),

    deleteHTML: builder.mutation({
      query: (p) => ({
        url: "/delete_email",
        method: "POST",
        params: {"id": p.id}
      })
    }),

    getRandomSummaries: builder.query({
      query: (p) => ({
        url: "/get_random_summaries",
        params: p,
        method: 'GET'
      })
    }),

    getRawRandomSummaries: builder.query({
      query: (p) => ({
        url: "/get_raw_random_summaries",
        params: p,
        method: 'GET'
      })
    }),

    getMemberProfile: builder.query({
      query: (p) => ({
        url: "/get_member_profile",
        params: p,
        method: 'GET'
      })
    }),

    getPostGresTables: builder.query({
      query: () => ({
        url: "/get_postgres_tables"
      })
    }),

    getPostGresTableDescription: builder.query({
      query: (p) => ({
        url: "/describe_postgres_table",
        params: p,
        method: 'GET'
      })
    }),

    getModelImagePG: builder.query({
      query: (p) => ({
        url: "/get_model_image_pg",
        params: p,
        method: 'GET'
      })
    }),
    
    getUserData: builder.query({
      query: (p) => ({
        url: "/get_user_data",
        params: p,
        method: 'GET'
      })
    }),

    getScheduledEvents: builder.query({
      query: (p) => ({
        url: "/get_scheduled_events",
        params: p,
        method: 'GET'
      })
    }),

    addScheduledEvent: builder.mutation({
      query: (p) => ({
        url: "/add_scheduled_event",
        body: p,
        method: 'POST'
      })
    }),

    editScheduledEvent: builder.mutation({
      query: (p) => ({
        url: "/edit_scheduled_event",
        body: p,
        method: 'POST'
      })
    }),

    deleteScheduledEvent: builder.mutation({
      query: (p) => ({
        url: `/delete_scheduled_event/${p.id}`,
        method: "DELETE"
      })
    }),

    getCheckSchedule: builder.query({
      query: () => ({
        url: "/get_check_schedule",
        method: 'GET'
      })
    }),

    getDashboard: builder.query({
      query: (p) => ({
        url: "/get_dashboard",
        method: 'GET',
        params: p
      })
    }),

    getAwsSesQuotas: builder.query({
      query: () => ({
        url: "/get_aws_ses_quotas",
        method: 'GET'
      })
    })

  })
});

// Export the auto-generated hooks for the endpoints
export const { 
  // System/health related hooks
  useGetHealthQuery,
  
  // User authentication and management
  useCreateUserMutation, useLoginMutation, useLazyGetUserDataQuery,
  
  // Live streams and models
  useLiveStreamsQuery, useLiveStreamModelCountsQuery,
  
  // Email generation and management
  useAddEmailMutation, useAllEmailsQuery, useEditEmailMutation, useGenerateEmailMutation, useSendEmailMutation, useImportHTMLemailMutation,
  
  // HTML handling
  useLazyGenerateHTMLQuery, useDeleteHTMLMutation, useChamaileonTokenMutation, useLazyJsonDownloadQuery, 
  
  // Image operations
  useDeleteImageMutation, useEditImageMutation, useUploadS3Mutation, useAllImagesQuery, 
  
  // Summaries
  useGetSummariesQuery, useLazyGetSummariesQuery, useLazyGetRawRandomSummariesQuery, useLazyGetRandomSummariesQuery,
  
  // Database and data queries
  useLazyGetMemberProfileQuery, useLazyGetPostGresTablesQuery, useLazyGetPostGresTableDescriptionQuery, useLazyGetModelImagePGQuery,
  
  // Schedule queries
  useLazyGetScheduledEventsQuery, useAddScheduledEventMutation, useEditScheduledEventMutation, useDeleteScheduledEventMutation, useLazyGetCheckScheduleQuery,

  // Dashboard
  useLazyGetDashboardQuery, useGetAwsSesQuotasQuery
} = api;
